import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';

import { ETF, ETFsResponse, Fund, FundsResponse } from './entities';
import { ETFRestful } from './restful';

interface ErrorEvent {
  error: SafeError;
  errorType: 'get_etfs_error' | 'get_etf_error' | 'get_funds_error' | 'get_fund_error';
  type: 'error';
}

interface GetETFsEvent {
  etfs: ETF[];
  type: 'get_etfs';
}

interface GetETFEvent {
  etf: ETF;
  type: 'get_etf';
}

interface GetFundsEvent {
  funds: Fund[];
  type: 'get_funds';
}

interface GetFundEvent {
  fund: Fund;
  type: 'get_fund';
}

export type ETFRestfulEvent = ErrorEvent | GetETFsEvent | GetETFEvent | GetFundsEvent | GetFundEvent;

interface ETFsFunctions {
  getETFs: ETFRequest['getETFs'];
  getETF: ETFRequest['getETF'];
}

export class ETFRequest extends ExtendedSubscribable<ETFRestfulEvent, ETFsFunctions> {
  private restful: ETFRestful;

  constructor(session: Session) {
    super();
    this.restful = new ETFRestful(session);
  }

  public getETFs = async (): SafePromise<{ data: ETFsResponse }> => {
    const response = await this.restful.getETFs();
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_etfs_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        etfs: response.ok?.data?.etfs ?? [],
        type: 'get_etfs',
      });
    }
    return response;
  };

  public getETF = async (id: number | string): SafePromise<{ data: ETF }> => {
    const response = await this.restful.getETF(id);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_etf_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        etf: response.ok.data,
        type: 'get_etf',
      });
    }
    return response;
  };

  public getFunds = async (): SafePromise<{ data: FundsResponse }> => {
    const response = await this.restful.getFunds();
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_funds_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        funds: response.ok?.data?.funds ?? [],
        type: 'get_funds',
      });
    }
    return response;
  };

  public getFund = async (id: number | string): SafePromise<{ data: Fund }> => {
    const response = await this.restful.getFund(id);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_fund_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        fund: response.ok.data,
        type: 'get_fund',
      });
    }
    return response;
  };

  public onSubscribe = (): ETFsFunctions => ({
    getETF: this.getETF,
    getETFs: this.getETFs,
  });
}
